// External libraries
import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

// Microsoft Graph Toolkit
import { Providers } from "@microsoft/mgt-element";

// Styles
import "./App.css";
import "./Themes/light-theme-custom.scss";
import "./Themes/light-theme.scss";
import "./Themes/theme-variables.scss";

// Hooks
import { useTheme } from "./hooks/useTheme";

// Internal components
import { CookieProxyProvider } from "./app/CustomGraphProxy";
import AuditTrail from "./components/Auditing/ViewAuditTrail";
import NewDataset from "./components/Datasets/NewDataset";
import ViewDataset from "./components/Datasets/ViewDataset";
import EditEntry from "./components/Entry/Manage/EditEntry";
import ViewEntry from "./components/Entry/View/ViewEntry";
import AtlasAppBar from "./components/Navigation/AppBar/AppBar";
import HamburgerMenu from "./components/Navigation/HamburgerMenu/HamburgerMenu";
import NotificationControl from "./components/Notification/NotificationControl";
import { ErrorBoundary } from "./components/Pages/ErrorScreen/ErrorBoundary";
import HomePage2 from "./components/Pages/Home/Homepage2";
import HomePage from "./components/Pages/Homepage";
import LoadingScreen from "./components/Pages/LoadingScreen/LoadingScreen";
import AuthCallback from "./components/Pages/Login/AuthCallback";
import LoginScreen from "./components/Pages/Login/LoginScreen";
import NewRole from "./components/Roles/NewRole";
import ViewRoles from "./components/Roles/ViewRoles";
import NewTemplate from "./components/Template/NewTemplate";
import ViewTemplates from "./components/Template/ViewTemplates";

// Utilities
import {
  ProtectedRoute,
  getPermissions,
  getRoles,
  groupIdsToNames,
} from "./components/Utilities/AuthorizationUtil";
import { graphUserByEmailUrl } from "./components/Utilities/GraphUserLookup";

// Redux
import { getUserLastEntries } from "./components/Entry/EntrySlice";

// Signals
import {
  allDatasets,
  allRoles,
  allTemplates,
  darkModeEnabled,
  isAdmin as isAdminSignal,
  isAuthenticated,
  newHomepageEnabled,
  userEmail as uEmail,
  userData,
  userPreferences,
} from "./components/Common/Store/CommonSignals";
import { showAtlasMenu } from "./components/Entry/Manage/EditEntrySignals";

function App() {
  useTheme();

  useEffect(() => {
    window.addEventListener("error", (e) => {
      if (e.message.includes("ResizeObserver loop")) {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div"
        );
        const resizeObserverErr = document.getElementById("webpack-dev-server-client-overlay");
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute("style", "display: none");
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute("style", "display: none");
        }
      }
    });
  }, []);

  const [hasDatasetEditAccess, setHasDatasetEditAccess] = useState(null);
  const [hasTemplateEditAccess, setHasTemplateEditAccess] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const params = new URLSearchParams(window.location.search);
  const entryOnly = params.get("entryOnly") === "true";
  showAtlasMenu.value = !entryOnly;
  uEmail.value = userData.value?.userEmail;

  // Fetch user preferences from backend
  const fetchUserPreferences = async () => {
    if (!userData.value?.userEmail) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/api/UserPreferences/${encodeURIComponent(
          userData.value.userEmail
        )}`
      );
      userPreferences.value = response.data;
      newHomepageEnabled.value = userPreferences.value.newHomepageEnabled;
      darkModeEnabled.value = userPreferences.value.darkModeEnabled || false;
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Failed to fetch user preferences:", error);
      }
    }
  };

  useEffect(() => {
    if (!isAuthenticated.value || !localStorage.getItem("atlas.sessionToken")) return;

    async function fetchRoles() {
      const response = await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/Role`);
      allRoles.value = response.data;
    }
    async function fetchTemplates() {
      const response = await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/Template`);
      allTemplates.value = response.data;
    }
    async function fetchDatasets() {
      const response = await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/Datasets`);
      allDatasets.value = response.data;
    }
    fetchRoles();
    fetchDatasets();
    fetchTemplates();
    fetchUserPreferences();
    setUserGroupsAndRoles();
  }, [isAuthenticated.value]);

  const handleAuthentication = async () => {
    async function fetchRoles() {
      const response = await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/Role`);
      allRoles.value = response.data;
    }
    setIsLoading(true);
    await fetchRoles();
    setUserGroupsAndRoles();
    isAuthenticated.value = true;
    await fetchUserPreferences();

    setTimeout(() => {
      setIsLoading(false);
    }, 200);
  };

  // Check auth when loading app
  useEffect(() => {
    const applyGroupsResult = (groupsResult) => {
      if (groupsResult.status !== "fulfilled") {
        console.error(
          "[restoreSession] Failed to fetch groups from Graph:",
          groupsResult.reason
        );
        return;
      }
      const graphNames = groupsResult.value.data?.groups ?? [];
      const graphIds = groupsResult.value.data?.groupIds ?? [];
      const resolvedNames =
        graphNames.length > 0 ? graphNames : groupIdsToNames(graphIds);
      userData.value = { ...userData.value, groups: resolvedNames };
    };

    const applyProfileResult = (profileResult) => {
      if (profileResult.status !== "fulfilled") {
        console.error(
          "[restoreSession] Failed to fetch profile from Graph:",
          profileResult.reason
        );
        return;
      }
      const user = profileResult.value.data?.value?.[0];
      if (!user) return;
      userData.value = {
        ...userData.value,
        officeLocation: user.officeLocation ?? "",
      };
    };

    const restoreSession = async () => {
      // /auth-callback handles its own token exchange — don't pre-empt it
      if (window.location.pathname === "/auth-callback") {
        setIsLoading(false);
        return;
      }

      const storedToken = localStorage.getItem("atlas.sessionToken");
      if (!storedToken) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_ENDPOINT}/auth/saml/validate`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${storedToken}`,
            },
          }
        );

        if (response.data?.isValid) {
          const userDataJson = response.data?.userData;
          const email = userDataJson.username;
          userData.value = {
            ...userData.value,
            userEmail: email,
            officeLocation: "",
            groups: [],
          };
          isAuthenticated.value = true;

          const [groupsResult, profileResult] = await Promise.allSettled([
            axios.get(
              `${process.env.REACT_APP_API_ENDPOINT}/api/GraphProxy/v1.0/me/groups`
            ),
            axios.get(graphUserByEmailUrl(email)),
          ]);

          applyGroupsResult(groupsResult);
          applyProfileResult(profileResult);

          const provider = new CookieProxyProvider(
            `${process.env.REACT_APP_API_ENDPOINT}/api/GraphProxy/v1.0`,
            async () => ({
              "X-User-Email": userData.value?.userEmail,
            })
          );
          Providers.globalProvider = provider;
          provider.initialize();

          setUserGroupsAndRoles();
        } else {
          localStorage.removeItem("atlas.sessionToken");
        }
      } catch (error) {
        console.error("Error validating session:", error);
        localStorage.removeItem("atlas.sessionToken");
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, []);

  //Set User Roles and permissions
  const setUserGroupsAndRoles = async () => {
    if (userData.value?.groups) {
      const roles = getRoles(userData.value.groups);
      userData.value = { ...userData.value, roles: roles };

      const permissions = getPermissions(userData.value.groups, userData.value.roles);
      userData.value = { ...userData.value, permissions: permissions };

      isAdminSignal.value = userData.value.groups?.includes("ATLAS_Admin") || false;

      if (userData.value?.userEmail) {
        dispatch(getUserLastEntries({ userId: userData.value.userEmail, number: 10 }));
      }
    }
  };

  useEffect(() => {
    let user = userData.value?.userEmail;

    if (user) {
      setHasDatasetEditAccess(
        allDatasets.value.some(
          (dataset) =>
            dataset.sharedPermission.some(
              (permission) => permission.entity.toLowerCase() === user.toLowerCase()
            ) ||
            dataset.coOwners.some((coOwner) => coOwner?.user?.toLowerCase() === user?.toLowerCase())
        )
      );
    }
  }, [allDatasets.value, userData.value]);

  useEffect(() => {
    let user = userData.value?.userEmail;

    if (user) {
      setHasTemplateEditAccess(
        allTemplates.value.some(
          (template) =>
            template.sharedPermission.some(
              (permission) => permission.entity.toLowerCase() === user.toLowerCase()
            ) ||
            template.coOwners.some(
              (coOwner) => coOwner?.user?.toLowerCase() === user?.toLowerCase()
            )
        )
      );
    }
  }, [allTemplates.value, userData.value]);

  if (isLoading) {
    return (
      <ErrorBoundary>
        <NotificationControl />
        <LoadingScreen />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <NotificationControl />
      <BrowserRouter>
        {!isAuthenticated.value ? (
          <Routes>
            <Route
              path="*"
              element={<LoginScreen handleAuthentication={handleAuthentication} />}
            />
            <Route
              path="/auth-callback"
              element={<AuthCallback handleAuthentication={handleAuthentication} />}
            />
          </Routes>
        ) : (
          <>
            <AtlasAppBar
              isAuthenticated={isAuthenticated.value}
              handleAuthentication={handleAuthentication}
            />
            <HamburgerMenu>
              <Routes>
                {/* Homepage */}
                <Route
                  path="/"
                  element={newHomepageEnabled.value ? <HomePage2 /> : <HomePage />}
                />
                {/* Entry URLs */}
                <Route
                  path="/new-entry"
                  element={
                    <ProtectedRoute
                      hasAccess={hasTemplateEditAccess || showAtlasMenu.value}
                      component="Create Entry"
                    >
                      <EditEntry />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/edit-entry"
                  element={
                    <ProtectedRoute
                      hasAccess={hasTemplateEditAccess}
                      component="Edit Entry"
                    >
                      <EditEntry />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/view-entry"
                  element={
                    <ProtectedRoute component="View Entry">
                      <ViewEntry />
                    </ProtectedRoute>
                  }
                />

                {/* Template URLs */}
                <Route
                  path="/templates"
                  element={
                    <ProtectedRoute component="View Template">
                      <ViewTemplates />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/templates/create"
                  element={
                    <ProtectedRoute component="Create Template">
                      <NewTemplate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/templates/edit"
                  element={
                    <ProtectedRoute
                      hasAccess={hasTemplateEditAccess}
                      component="Edit Template"
                    >
                      <NewTemplate />
                    </ProtectedRoute>
                  }
                />

                {/* Dataset URLs */}
                <Route
                  path="/datasets"
                  element={
                    <ProtectedRoute component="View Dataset">
                      <ViewDataset />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/datasets/create"
                  element={
                    <ProtectedRoute component="Create Dataset">
                      <NewDataset />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/datasets/edit"
                  element={
                    <ProtectedRoute
                      hasAccess={hasDatasetEditAccess}
                      component="Edit Dataset"
                    >
                      <NewDataset />
                    </ProtectedRoute>
                  }
                />

                {/* Role URLs */}
                <Route
                  path="/roles"
                  element={
                    <ProtectedRoute component="View Roles">
                      <ViewRoles />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/roles/create"
                  element={
                    <ProtectedRoute component="Create Role">
                      <NewRole />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/roles/edit"
                  element={
                    <ProtectedRoute component="Edit Role">
                      <NewRole />
                    </ProtectedRoute>
                  }
                />

                {/* Audit Log URL */}
                <Route
                  path="/audit-log"
                  element={
                    <ProtectedRoute component="View Audit Trail">
                      <AuditTrail />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/logout"
                  element={
                    <Navigate
                      to="/login"
                      state={{ logout: true }}
                      replace={true}
                    />
                  }
                />
              </Routes>
            </HamburgerMenu>
          </>
        )}
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
